import { MasterpassJwtClaims } from 'Models/MasterpassSdkModels';
import MasterPassServices from '../MasterPassServices';
import { authorizeMasterpassSdk, decodeMasterpassJwt, flowDirectable } from '../MasterpassBootstrap';
import { deriveMasterpassAccountStatus } from '../MasterpassResponse';
import { MasterpassAdapter, MasterpassAuthorizeParams, MasterpassSession } from './Types';

/**
 * Masterpass Javascript SDK Web v1.0.7 adaptörü (`window.Masterpass`).
 *
 * Bu dosya YENİ DAVRANIŞ EKLEMEZ — mevcut v2 kodunu ortak arayüzün arkasına
 * taşır. Metotların tamamı `Utils/MasterPassServices`'e olduğu gibi devreder,
 * dolayısıyla v2 akışı adaptör devreye girdikten sonra da birebir aynı çalışır.
 */

/**
 * Token'ın süresi dolmadan önce bırakılan pay.
 * İstek yolda iken token'ın geçersizleşmesini önler.
 */
const TOKEN_EXPIRY_SAFETY_MS = 60000;

let session: MasterpassSession | undefined;

const V2Adapter: MasterpassAdapter = {
  version: 'v2',

  get session() {
    return session;
  },

  authorize: ({ token, merchantId, phone, customerNumber }: MasterpassAuthorizeParams) => {
    const claims: MasterpassJwtClaims | undefined = authorizeMasterpassSdk(token, merchantId);

    if (!claims) {
      session = undefined;
      return undefined;
    }

    // Kimlik alanları JWT'den okunur; claim gelmezse telefon numarasına düşülür.
    session = {
      version: 'v2',
      token,
      merchantId: merchantId || claims.MerchantId,
      accountKey: claims.AccountKey ?? phone,
      accountKeyType: 'Msisdn',
      // ⚠️ MÜŞTERİ NUMARASI ÖNCELİKLİ — JWT'nin `UserId` claim'i DEĞİL.
      // Claim şifreli bir değer taşıyor (`OeJ1cexyYMJ6Bq6uWSKW6w==`) ve Masterpass
      // onu hesaba bağlayamadığında `/account/api/Card` çağrıları
      // `404 ACCOUNT_NOT_FOUND` dönüyordu; 404 cevabının `details` alanı servisin
      // içeride müşteri numarası (`UserId=1157406`) beklediğini gösteriyor.
      // Düz müşteri numarasıyla akış çalışıyor.
      userId: customerNumber != null && customerNumber !== '' ? String(customerNumber) : claims.UserId ?? phone,
      claims,
    };

    return session;
  },

  /**
   * JWT oturumluktur; süresi dolmadıkça yeni bir işlemde tekrar kullanılabilir.
   *
   * ⚠️ Buradaki kontrol yalnızca SÜRE kontrolüdür. Token'ın işlem KAPSAMI
   * (ödeme / hesap) ayrı bir konudur ve saga'da `tokenIsPaymentScoped` ile
   * yönetilir — ödeme için alınmış token hesap işlemlerinde kullanılırsa
   * Masterpass `403 AUTHENTICATION_FAILED` döner.
   */
  canReuseToken: (token?: string) => {
    if (!token) {
      return false;
    }

    const claims = decodeMasterpassJwt(token);

    return !!claims?.exp && claims.exp * 1000 > Date.now() + TOKEN_EXPIRY_SAFETY_MS;
  },

  flowToken: () => flowDirectable()?.token,

  flow3d: () => {
    const flow = flowDirectable();

    return { url3d: flow?.url3d, url3dSuccess: flow?.url3dSuccess, url3dFail: flow?.url3dFail };
  },

  accountAccess: MasterPassServices.accountAccess,

  /**
   * ⚠️ v2'de hesap durumu ve kart listesi AYNI cevaptan gelir, bu yüzden
   * `accountAccess` ile birebir aynıdır. (v1'de ayrı bir servistir.)
   */
  checkAccount: MasterPassServices.accountAccess,

  /**
   * ⚠️ v2 cevabında hazır bir "hesap durumu" alanı YOKTUR; durum cevabın farklı
   * alanlarından (`accountState`, `isAccountLinked`, `cards`) türetilir.
   */
  accountStatus: deriveMasterpassAccountStatus,
  addCard: MasterPassServices.addCard,
  removeCard: MasterPassServices.removeCard,
  linkToMerchant: MasterPassServices.linkToMerchant,
  completeRegistration: (request, callback, token = null) =>
    MasterPassServices.completeRegistration(request, callback, token),

  payment: MasterPassServices.payment,
  directPayment: MasterPassServices.directPayment,
  registerAndPurchase: MasterPassServices.registerAndPurchase,

  verifyOtp: MasterPassServices.verifyOtp,
  resendOtp: MasterPassServices.resendOtp,

  recurringOrder: MasterPassServices.recurringOrder,
  recurringOrderUpdate: MasterPassServices.recurringOrderUpdate,
  recurringOrderDelete: MasterPassServices.recurringOrderDelete,
};

export default V2Adapter;
